import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";

const parkedIcon = new L.DivIcon({
  className: "track-pin",
  html: '<div style="background:#059669;width:26px;height:26px;border-radius:13px 13px 13px 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;color:white;border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,0.35)"><span style="transform:rotate(45deg);font-size:12px">🚛</span></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], map.getZoom() || 12);
  }, [lat, lng, map]);
  return null;
}

export default function PublicTrack() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/track/${token}`);
      setData(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "This tracking link is no longer available.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30000); // keep it fresh
    return () => clearInterval(id);
  }, [fetchData]);

  // Reflect the unit number in the browser tab / link preview title.
  useEffect(() => {
    if (data?.unit_number) {
      document.title = `Unit ${data.unit_number} · Live Tracking · ${data.company_name || "Rep Fleet"}`;
      const setMeta = (prop, val) => {
        let el = document.querySelector(`meta[property="${prop}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
        el.setAttribute("content", val);
      };
      setMeta("og:title", `Unit ${data.unit_number} — Live Tracking`);
      setMeta("og:description", `${data.location_address || "Live location"} · ${data.company_name || "Rep Fleet"}`);
    }
  }, [data]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 font-mono text-sm text-slate-500" data-testid="track-loading">
        Loading location…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6" data-testid="track-error">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 max-w-md text-center">
          <div className="text-3xl mb-3">📍</div>
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Tracking unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const hasCoords = data.latitude != null && data.longitude != null;
  const center = hasCoords ? [data.latitude, data.longitude] : [39.8283, -98.5795];
  const updated = data.location_updated_at ? new Date(data.location_updated_at) : null;
  const expires = data.expires_at ? new Date(data.expires_at) : null;
  const isParked = !!data.is_parked;

  return (
    <div className="relative w-screen h-screen overflow-hidden" data-testid="public-track-page">
      {/* Map */}
      <div className="absolute inset-0 z-0">
        <MapContainer center={center} zoom={hasCoords ? 12 : 4} style={{ height: "100%", width: "100%" }} scrollWheelZoom zoomControl={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {hasCoords && <Marker position={center} icon={parkedIcon} />}
          {hasCoords && <Recenter lat={data.latitude} lng={data.longitude} />}
        </MapContainer>
      </div>

      {/* Floating info card */}
      <div className="absolute top-4 left-4 z-[1000] w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden" data-testid="track-card">
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div className="text-lg font-semibold text-slate-900">
              Vehicle <span className="font-bold">{data.unit_number}</span>
            </div>
            <button
              onClick={copyLink}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              data-testid="track-copy-link"
            >
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-slate-900">Current Location</div>
            <div className="text-sm text-slate-600 mt-1">{data.location_address || "Location unavailable"}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span
                className={`inline-block w-2 h-2 rounded-full ${isParked ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ boxShadow: isParked ? "0 0 0 3px rgba(245,158,11,0.2)" : "0 0 0 3px rgba(16,185,129,0.2)" }}
              />
              <span className={`text-xs font-semibold ${isParked ? "text-amber-600" : "text-emerald-600"}`} data-testid="track-status">
                {isParked ? "Parked" : "Live"}
              </span>
              {updated && (
                <span className="text-xs text-slate-400" data-testid="track-updated">
                  · Updated {updated.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-slate-900">Details</div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium w-24">Vehicle Model</div>
              <div className="text-sm text-slate-700">{data.vehicle_model || "—"}</div>
            </div>
          </div>

          {expires && (
            <div className="mt-4 bg-slate-50 border border-slate-100 rounded-md px-4 py-2.5 text-center">
              <span className="text-xs text-slate-500">This link will expire on {expires.toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">Powered by</div>
          <div className="font-display font-black text-slate-900 tracking-tight">{data.company_name}</div>
        </div>
      </div>
    </div>
  );
}
